import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Sparkles, Leaf, Moon, Sun } from 'lucide-react';

const experiences = [
  {
    icon: Moon,
    title: "Sacred Ceremony",
    description: "Experience the profound depths of ayahuasca in a traditional ceremonial setting, guided by experienced facilitators who honor both ancient wisdom and modern safety protocols."
  },
  {
    icon: Leaf,
    title: "Amazon Immersion",
    description: "Connect with the pristine rainforest ecosystem. Our retreat center is nestled deep within the Amazon, offering complete immersion in nature's healing embrace."
  },
  {
    icon: Sun,
    title: "Integration Support",
    description: "Your journey doesn't end with the ceremony. We provide comprehensive integration guidance to help you process and apply your insights to daily life."
  },
  {
    icon: Sparkles,
    title: "Transformative Journey",
    description: "Each experience is unique. Participants often report profound spiritual insights, emotional healing, and a renewed connection to their authentic selves."
  }
];

const ExperienceCard = ({ experience, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const Icon = experience.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="glass-card rounded-2xl p-8 card-hover"
      data-testid={`experience-card-${index}`}
    >
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-neon-cyan/20 to-neon-magenta/20 flex items-center justify-center mb-6 border border-egyptian-gold/30">
        <Icon className="w-8 h-8 text-egyptian-gold" />
      </div>
      
      <h3 className="font-heading font-bold text-2xl text-white mb-4 uppercase tracking-wide">
        {experience.title}
      </h3>
      
      <p className="font-body text-gray-400 leading-relaxed">
        {experience.description}
      </p>
    </motion.div>
  );
};

export const ExperienceSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section id="experience" className="py-24 relative" data-testid="experience-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="font-accent text-neon-cyan text-sm tracking-[0.3em] mb-4">
            THE JOURNEY AWAITS
          </p>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl uppercase tracking-tight text-white mb-6">
            What <span className="neon-gold">Awaits</span> You
          </h2>
          <p className="font-body text-gray-400 text-lg max-w-2xl mx-auto">
            A carefully curated experience combining ancestral knowledge with contemporary understanding, 
            ensuring your journey is both profound and safe.
          </p>
        </motion.div>

        {/* Experience Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {experiences.map((experience, index) => (
            <ExperienceCard key={index} experience={experience} index={index} />
          ))}
        </div>

        {/* Bottom Image Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 relative rounded-3xl overflow-hidden"
        >
          <img
            src="https://images.unsplash.com/photo-1693754078640-8bd19929a488?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxiaW9sdW1pbmVzY2VudCUyMGFtYXpvbiUyMGp1bmdsZSUyMG5pZ2h0JTIwbmVvbnxlbnwwfHx8fDE3NzA0NTc2MTl8MA&ixlib=rb-4.1.0&q=85"
            alt="Amazon Jungle Night"
            className="w-full h-80 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-jungle-dark via-transparent to-transparent" />
          <div className="absolute bottom-8 left-8 right-8">
            <p className="font-heading text-3xl text-white uppercase tracking-wide">
              Where <span className="text-egyptian-gold">Technology</span> Meets <span className="text-neon-cyan">Tradition</span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
